import { ALL_PERMISSIONS } from './constants'

/**
 * Pure utility functions for permission checking
 * These functions don't depend on Vue reactivity for better testability
 */

export interface PermissionUser {
  roles?: string[]
  permissions?: string[]
}

/**
 * Check if user has admin role
 */
export function isUserAdmin(user: PermissionUser | null): boolean {
  return user?.roles?.includes('ROLE_ADMIN') || false
}

/**
 * Create a Set cache from permissions array for O(1) lookup
 */
export function createPermissionCache(permissions: string[] | undefined | null): Set<string> {
  if (!permissions || permissions.length === 0) {
    return new Set<string>()
  }
  return new Set(permissions)
}

/**
 * Check if permission exists in a permission set (cache)
 */
export function hasPermissionInCache(cache: Set<string>, permission: string): boolean {
  return cache.has(ALL_PERMISSIONS) || cache.has(permission)
}

/**
 * Optimized central method for checking permissions
 * Uses Set cache for O(1) lookup instead of O(n) array.includes()
 *
 * @param user User object with roles and permissions
 * @param permission Required permission to check
 * @param projectPermissions Optional project permissions to check
 * @returns Boolean indicating whether user has the permission
 */
export function checkPermission(
  user: PermissionUser | null,
  permission: string,
  projectPermissions?: string[] | null
): boolean {
  // 1. Admin has all permissions
  if (isUserAdmin(user)) {
    return true
  }

  // 2. Check global user permissions (with cache)
  const userPermissionsCache = createPermissionCache(user?.permissions)
  if (userPermissionsCache.size > 0) {
    if (hasPermissionInCache(userPermissionsCache, permission)) {
      return true
    }
  }

  // 3. Check project permissions (if provided)
  if (projectPermissions && projectPermissions.length > 0) {
    const projectPermissionsCache = createPermissionCache(projectPermissions)
    if (hasPermissionInCache(projectPermissionsCache, permission)) {
      return true
    }
  }

  // No permission found
  return false
}

/**
 * Fast check if user has global permission (without project)
 * @param user User object with roles and permissions
 * @param permission Required permission to check
 * @returns Boolean indicating whether user has global permission
 */
export function checkGlobalPermission(
  user: PermissionUser | null,
  permission: string
): boolean {
  if (isUserAdmin(user)) {
    return true
  }

  const userPermissionsCache = createPermissionCache(user?.permissions)
  return hasPermissionInCache(userPermissionsCache, permission)
}
